import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

export interface Pagination {
  size: number;
  page: number;
}

@Component({
  selector: 'app-custom-paginator',
  standalone: true,
  imports: [
    CommonModule,
    DropdownModule,
    PaginatorModule,
    InputTextModule,
    TranslateModule,
    DialogModule,
    ReactiveFormsModule
  ],
  templateUrl: './custom-paginator.component.html',
  styleUrl: './custom-paginator.component.scss'
})
export class CustomPaginatorComponent {
  @Input() isNewUI: boolean = false;
  @Input() isContactManagement: boolean = false;
  @Input() pagination: Pagination = { page: 0, size: 10 };
  @Input() totalRecords = 0;
  @Input() options: number[] = [5, 10, 20];
  @Input() selectedRows: number = 0;
  @Input() selectedRowsAction?: TemplateRef<any>;
  @Input() module: string = 'contacts';
  @Input() type: string = '';

  @Output() paginationChange = new EventEmitter<Pagination>();
  @Output() onChange = new EventEmitter();
  @Output() selectAllRows = new EventEmitter();
  @Output() unSelectAllRows = new EventEmitter();
  @Output() selectRange = new EventEmitter<{ from: number; to: number }>();

  jumpTo = 1;
  isVisibleSelectRange: boolean = false;

  selectRangeForm = this.fb.group({
    from: this.fb.control('', [Validators.required]),
    to: this.fb.control('', [Validators.required])
  });

  constructor(private fb: FormBuilder) {}

  onJumpPage() {
    if (this.jumpTo <= 0) {
      this.jumpTo = 1;
    }

    const maxPage = Math.ceil(this.totalRecords / this.pagination.size);

    if (this.jumpTo <= maxPage) {
      this.pagination.page = this.jumpTo - 1;
    } else {
      this.jumpTo = maxPage;
      this.pagination.page = maxPage - 1;
    }

    if (this.jumpTo <= 0) {
      this.jumpTo = 1;
      this.pagination.page = 0;
    }

    this.paginationChange.emit(this.pagination);
    this.onChange.emit();
  }

  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.pagination.size);
  }

  get pageNumbers(): number[] {
    const totalPages = this.totalPages;
    const currentPage = this.pagination.page;
    const pages: number[] = [];

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    if (currentPage <= 3) {
      pages.push(0, 1, 2, 3, 4, -1, totalPages - 1);
    } else if (currentPage >= totalPages - 4) {
      pages.push(0, -1, totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1);
    } else {
      pages.push(0, -1, currentPage - 1, currentPage, currentPage + 1, -1, totalPages - 1);
    }

    return pages;
  }

  onPageChange(page: number): void {
    if (page === -1) return;
    if (page !== this.pagination.page) {
      this.pagination.page = page;
      this.paginationChange.emit(this.pagination);
      this.onChange.emit();
    }
  }

  onSizeChange(size: number): void {
    this.pagination.size = size;
    this.pagination.page = 0;
    this.paginationChange.emit(this.pagination);
    this.onChange.emit();
  }

  onSelectAllRows() {
    this.selectAllRows.emit();
  }

  onUnSelectAllRows() {
    this.selectRange.emit({ from: 0, to: 0 });
  }

  onShowSelectRange() {
    this.isVisibleSelectRange = true;
  }

  onHideSelectRange() {
    this.isVisibleSelectRange = false;
  }

  onCancel() {
    this.isVisibleSelectRange = false;
  }

  onSelectRange() {
    if (this.selectRangeForm.invalid) return;
    const from = +(this.selectRangeForm.value.from || 0);
    const to = +(this.selectRangeForm.value.to || 0);
    this.selectRange.emit({ from, to });
    this.isVisibleSelectRange = false;
  }
}
